import type BaseHttpService from "../../client/BaseHttpService";
import { ApiError, type ApiResponse } from "../../client/types";
import type { ConfigTheming, PartnerSettings } from "./types";

export * from "./types";

export type SolutionConfig<TApp = Record<string, unknown>> = PartnerSettings & ConfigTheming & TApp;

type SolutionConfigResponse = {
	solutionKey: string;
	partnerCode: string | null;
	complete: boolean;
	values: {
		partner: PartnerSettings | null;
		theme: ConfigTheming | null;
		[key: string]: unknown;
	};
};

export class ConfiguratorApi {
	constructor(
		private readonly http: BaseHttpService,
		private readonly solutionKey: string,
		private readonly globalModuleKey: string = `${solutionKey}Global`,
	) {}

	async getConfig<TApp = Record<string, unknown>>(
		identifier: string,
		locationId?: string,
	): Promise<SolutionConfig<TApp>> {
		const query = locationId ? `?locationId=${encodeURIComponent(locationId)}` : "";
		const response = await this.http.get<ApiResponse<SolutionConfigResponse>>(
			"Fetching solution config",
			`/config/solutions/${this.solutionKey}/${identifier}${query}`,
		);
		const { values, complete } = response.data;
		if (!complete) {
			throw new ApiError(`Incomplete config for solution "${this.solutionKey}"`, { errorCode: "CONFIG_INCOMPLETE" });
		}
		return {
			...(values.partner ?? {}),
			...(values.theme ?? {}),
			...((values[this.solutionKey] ?? {}) as TApp),
			...((values[this.globalModuleKey] ?? {}) as Record<string, unknown>),
		} as SolutionConfig<TApp>;
	}

	async getGlobalConfig<T = Record<string, unknown>>(): Promise<T | null> {
		const response = await this.http.get<ApiResponse<SolutionConfigResponse>>(
			"Fetching global solution config",
			`/config/solutions/${this.solutionKey}`,
		);
		const { values, complete } = response.data;
		if (!complete) return null;
		return (values[this.globalModuleKey] ?? null) as T | null;
	}
}
